import { SubscriptionCompetitionsPrizesPageContext } from "./context";
import React, { useEffect, useState } from "react";
import { useCompetitionByType } from "../../../redux/features/categories/selectors";
import { getCompetitionDetails } from "./subscription-competitions-prizes.service";
import { getUserSubscriptionPlan } from "@/app/(general-header)/subscription/subscription-service";

import { useAppDispatch } from "@/app/redux/hooks";
import { CompetitionData } from "@/app/components/ActiveCompetitions/types";
import {
  publishModalClasses,
  publishModalData,
  publishModalProps,
  publishModalVisibility,
} from "@/app/redux/features/modal/modalSlice";
import SubscriptionPlansModal from "@/app/(custom-header)/subscriber-competitions/components/subscriptionPlansModal";
import { getSubscriptionLobbyContent } from "@/app/(custom-header)/subscriber-competitions/subscription-competitions-service";
import { getBotbPrizeStraplineTexts } from "@/app/common-service";
import { CompetitionPrizeTypes } from "@/app/(general-header)/management-subscription/types";
import { getAvailableSubscriptions } from "@/app/(general-header)/botb-pass/subscription-service";
import SubscribtionCompetitionModalContent from "@/app/(custom-header)/prizes/[prizeName]/subscribtionCompetitionModalContent";

const SubscriptionCompetitionsPrizesProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const [isLobbyContentLoading, setIsLobbyContentLoading] = useState(true);
  const [lobbyContent, setLobbyContent] = useState<SubscriptionLobbyContent>({
    pastWinnerEndDate: "",
    pastWinnerStartDate: "",
    subSubcribedHeader: { disclaimer: "", subtitle: "", title: "" },
    unsubSubcribedHeader: { perks: [], subtitle: "", title: "" },
    youveEntered: [],
  });
  const [isAlreadySubscribed, setIsAlreadySubscribed] = useState(false);
  const [activeCompetition, setActiveCompetition] = useState<CompetitionData>({
    AdditionalCashText: "",
    CompetitionId: 0,
    CompetitionPictureTypeId: 0,
    CtaAddToBasketPrizeId: 0,
    CtaText: "",
    CtaTextValidated: "",
    CtaTicketValue: "",
    CurrencySymbol: "",
    EndDateTime: "",
    EndDate: "",
    FormattedOriginalPrice: "",
    FormattedPrice: "",
    FormattedPriceWithoutCurrency: "",
    HasInstantWin: false,
    ImageDoubleUrl: "",
    ImageUrl: "",
    IsRewardCompetition: false,
    IsSinglePrizeMode: false,
    MultiBuyLowerPercent: 0,
    MultiBuyLowerQuantity: 0,
    MultiBuyUpperPercent: 0,
    MultiBuyUpperQuantity: 0,
    NavigationColour: "",
    OverrideDiscountValue: 0,
    PageUrl: "",
    PercentOffValue: 0,
    PostalEntryText: "",
    SupportsMultibuy: false,
    TicketText: "",
    Title: "",
    UseDailyPopupForCompetitionId: 0,
    SubtitleText: "",
    SubtitleTextValidated: "",
    OverrideCompetitionTypeId: null,
    CompetitionTypePrefix: "",
    IsMobileOnlyCompetition: false,
    IsMobileAppOnly: false,
    IsMultibuyEnabled: false,
    FeaturedPrizeId: 0,
    FeaturedPrizeModel: "",
    FeaturedPrizeThumbnail: "",
    MobileTitle: "",
  });
  const [competitionDetails, setCompetitionDetails] = useState<SubscriptionCompetitionDetails>({
    prizes: [],
    promoHeaderImageUrls: [],
    infoPopupContent: "",
  });
  const [prizeStraplineTexts, setPrizeStraplineTextsState] = useState<CompetitionPrizeTypes>({
    PrizeStraplineText1: "",
    PrizeStraplineText2: "",
    PrizeStraplineText3: "",
  });
  const [subscriptionPackages, setSubscriptionPackages] = useState<Package[]>([]);
  const [isPrizeStraplineTextsLoading, setIsPrizeStraplineTextsLoading] = useState(true);
  const [isCompetitionDetailsLoading, setIsCompetitionDetailsLoading] = useState(true);
  const category = useCompetitionByType("subscriberCompetitions");
  useEffect(() => {
    const abortController = new AbortController();
    getSubscriptionLobbyContent(abortController)
      .then(res => {
        setLobbyContent(res);
        setIsLobbyContentLoading(false);
      })
      .catch(err => console.error(err));
    getBotbPrizeStraplineTexts(abortController)
      .then(res => res.json())
      .then((res: CompetitionPrizeTypes) => {
        setPrizeStraplineTextsState(res);
        setIsPrizeStraplineTextsLoading(false);
      })
      .catch(err => console.error(err));
    getAvailableSubscriptions(abortController)
      .then(res => res.json() as Promise<Package[]>)
      .then(setSubscriptionPackages)
      .catch(err => {
        console.error(err);
      });
    return () => abortController.abort();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const res = await getUserSubscriptionPlan();
        const response = (await res.json()) as Awaited<SubscriptionPlanUser>;
        setIsAlreadySubscribed(!!response);
      } catch (err) {
        setIsAlreadySubscribed(false);
      }
    })();
  }, []);
  useEffect(() => {
    if (category?.competitions?.length) {
      const competition = category.competitions.find(comp => comp.PageUrl === location.pathname);
      if (competition) {
        setActiveCompetition(competition);
        getCompetitionDetails(competition.CompetitionId)
          .then(res => {
            setCompetitionDetails(res);
            setIsCompetitionDetailsLoading(false);
          })
          .catch(err => console.error(err));
      }
    }
  }, [category]);

  const openTabContent = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    dispatch(
      publishModalData({
        modalData: <SubscribtionCompetitionModalContent />,
      })
    );
    dispatch(
      publishModalClasses({
        classNames: "rounded-2xl lg:w-[450px] xs:w-[95%] relative",
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  const onViewPlansClick = (place?: string) => {
    dispatch(
      publishModalData({
        modalData: <SubscriptionPlansModal place={place} />,
      })
    );
    dispatch(
      publishModalProps({
        withOverlay: false,
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  return (
    <SubscriptionCompetitionsPrizesPageContext.Provider
      value={{
        competitionDetails,
        isAlreadySubscribed,
        category,
        isCompetitionDetailsLoading,
        activeCompetition,
        openTabContent,
        onViewPlansClick,
        lobbyContent,
        isLobbyContentLoading,
        prizeStraplineTexts,
        isPrizeStraplineTextsLoading,
        subscriptionPackages,
      }}>
      {children}
    </SubscriptionCompetitionsPrizesPageContext.Provider>
  );
};

export default SubscriptionCompetitionsPrizesProvider;
